'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import type { BlogPost } from '@/content/blog/types';
import BlogTransitionOverlay from './BlogTransitionOverlay';
import BlogGrid from './BlogGrid';

interface BlogInteractiveProps {
  posts: BlogPost[];
  lang?: 'en' | 'de';
}

export default function BlogInteractive({ posts, lang = 'en' }: BlogInteractiveProps) {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'de'>(lang);
  const [transitionActive, setTransitionActive] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const targetSlugRef = useRef<string>('');
  const router = useRouter();

  useEffect(() => {
    // Update language if prop changes
    setCurrentLanguage(lang);

    const handleLanguageChange = (event: CustomEvent<{ language: 'en' | 'de' }>) => {
      setCurrentLanguage(event.detail.language);
    };

    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);

  const handleCardClick = (slug: string, event: React.MouseEvent) => {
    // Store click position for transition animation
    setClickPosition({ x: event.clientX, y: event.clientY });
    targetSlugRef.current = slug;
    setTransitionActive(true);

    // Navigate after transition animation
    setTimeout(() => {
      router.push(`/blog/${slug}`);
    }, 600);
  };

  const translations = {
    en: {
      heading: 'Blog',
      subheading: 'Insights on AI, Content Strategy and Digital Innovation',
    },
    de: {
      heading: 'Blog',
      subheading: 'Einblicke in KI, Content-Strategie und digitale Innovation',
    },
  };

  return (
    <>
      <section className="relative z-10 min-h-screen px-4 pt-32 pb-20">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h1 
              className="text-5xl font-bold mb-6"
              style={{
                fontFamily: '"Exo 2", sans-serif',
                color: '#00b8ff'
              }}
            >
              {translations[currentLanguage].heading}
            </h1>
            <p 
              className="text-3xl font-semibold"
              style={{
                fontFamily: '"Exo 2", sans-serif',
                color: '#16c55f'
              }}
            >
              {translations[currentLanguage].subheading}
            </p>
          </div>

          <BlogGrid
            posts={posts}
            currentLanguage={currentLanguage}
            onCardClick={handleCardClick}
          />
        </div>
      </section>

      {transitionActive && (
        <BlogTransitionOverlay
          clickPosition={clickPosition}
          onComplete={() => setTransitionActive(false)}
        />
      )}
    </>
  );
}
